import {
  Calendar,
  DollarSign,
  FileText,
  FolderOpen,
  Image,
  Instagram,
  Linkedin,
  LogOut,
  Menu,
  MessageCircle,
  Plus,
  Receipt,
  Settings,
  Upload,
  Users,
  X,
} from "lucide-react";
import React, { useEffect } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import { useAdminStore } from "../../context/admin-store";
import { useCategory } from "../../context/category-store";
import { getToken, isAuthenticated } from "../../services/auth.service";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import AccountMenu from "./AccountMenu";

// Navigation items configuration
const navigationItems = [
  {
    id: "welcome-manager",
    title: "Página Inicial",
    icon: FileText,
    path: "welcome-manager",
  },
  {
    id: "community",
    title: "Comunidade",
    icon: MessageCircle,
    path: "community",
  },
  {
    id: "education",
    title: "Área Educacional",
    icon: FileText,
    path: "education",
  },
  {
    id: "events",
    title: "Eventos",
    icon: Calendar,
    path: "events",
  },
  {
    id: "expositors",
    title: "Expositores",
    icon: Users,
    path: "expositors",
  },
  {
    id: "pre-subscriptions",
    title: "Pré-inscrições",
    icon: FileText,
    path: "pre-subscriptions",
  },
  {
    id: "subscriptions",
    title: "Aprovações",
    icon: FileText,
    path: "subscriptions",
  },
  {
    id: "comissions",
    title: "Comissões",
    icon: DollarSign,
    path: "comissions",
  },
  {
    id: "documents",
    title: "Documentos",
    icon: FolderOpen,
    path: "documents",
  },
  {
    id: "receipts",
    title: "Comprovantes",
    icon: Receipt,
    path: "receipts",
  },
  {
    id: "gallery",
    title: "Galeria",
    icon: Image,
    path: "gallery",
  },

  // {
  //   id: "event-type",
  //   title: "Tipos de Evento",
  //   icon: BarChart3,
  //   path: "event-type",
  // },
];

const secondaryNavigationItems = [
  {
    id: "categories",
    title: "Adicionar Categoria",
    icon: Plus,
    path: "categories",
  },
  {
    id: "pix",
    title: "Alterar chave PIX",
    icon: Settings,
    path: "pix",
  },
  {
    id: "evolution",
    title: "WhatsApp (Evolution)",
    icon: Settings,
    path: "evolution",
  },
  {
    id: "kommo-config",
    title: "Kommo CRM",
    icon: Settings,
    path: "kommo-config",
  },
  {
    id: "kommo-import",
    title: "Importar CSV (Kommo)",
    icon: Upload,
    path: "kommo-import",
  },
];

export default function AdminDashboard({ children }: any) {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [whatsappConnected, setWhatsappConnected] = React.useState<
    boolean | null
  >(null);
  const data = useAdminStore((state) => state.fetch);
  const cat = useCategory((state) => state.fetch);
  const location = useLocation();

  useEffect(() => {
    data();
    cat();
  }, [data, cat]);

  useEffect(() => {
    const apiUrl = (process.env.REACT_APP_API_URL || "").trim();
    if (!apiUrl) return;

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        // Busca a config salva no banco (mesma usada pela tela
        // WhatsApp/Evolution) e consulta o status direto do navegador,
        // pois chamadas servidor-a-servidor para o Evolution podem ser
        // bloqueadas por WAF/firewall enquanto o navegador funciona normalmente.
        const configRes = await fetch(`${apiUrl}/evolution-config`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!configRes.ok) throw new Error("config request failed");
        const config = await configRes.json();

        const baseUrl = String(config?.baseUrl || "").replace(/\/+$/, "");
        const apiKey = String(config?.apiKey || "").trim();
        const instance = String(config?.instance || "").trim();
        if (!baseUrl || !apiKey || !instance) {
          if (!cancelled) setWhatsappConnected(false);
          return;
        }

        const stateRes = await fetch(
          `${baseUrl}/instance/connectionState/${encodeURIComponent(instance)}`,
          { headers: { apikey: apiKey } },
        );
        if (!stateRes.ok) throw new Error("state request failed");
        const stateJson = await stateRes.json();
        const state = stateJson?.instance?.state ?? stateJson?.state;
        if (!cancelled) setWhatsappConnected(state === "open");
      } catch {
        if (!cancelled) setWhatsappConnected(false);
      }
    };

    fetchStatus();
    const id = window.setInterval(fetchStatus, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!isAuthenticated()) {
    return <Navigate to={"/"} replace={true} />;
  }

  const isActiveRoute = (path: string) => {
    // Remove trailing slash and get the last segment of the pathname
    const currentPath = location.pathname.replace(/\/$/, "");
    const pathSegments = currentPath.split("/");
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Check if the last segment matches exactly with the path
    return lastSegment === path;
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  const PRACERIA_INSTAGRAM = "praceriaoficial";
  const PRACERIA_TIKTOK = "praceria";
  const getInstagram = () => {
    const handle = PRACERIA_INSTAGRAM.replace(/^@/, "");
    const href = `https://www.instagram.com/${handle}/`;
    const display = `Instagram`;
    return { href, display };
  };
  const getTikTok = () => {
    const handle = PRACERIA_TIKTOK.replace(/^@/, "");
    const href = `https://www.tiktok.com/@${handle}`;
    const display = `TikTok`;
    return { href, display };
  };
  const getLinkedin = () => {
    const href = "https://www.linkedin.com/company/praceria";
    const display = "Linkedin";
    return { href, display };
  };

  const getFacebook = () => {
    const href = "https://www.facebook.com/praceriaoficial";
    const display = "Facebook";
    return { href, display };
  };

  return (
    <div className="max-h-screen h-full bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 w-80 bg-gradient-to-b from-[#24324D] to-[#1a2538] overflow-y-auto shadow-2xl transform transition-transform duration-300 ease-in-out custom-scrollbar
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0 lg:static lg:inset-0
      `}
      >
        {/* Logo and Close Button */}
        <div className="flex items-center justify-center h-32 px-6 border-b border-white/20">
          <Link to="/dashboard" className="flex items-center space-x-3">
            <img src={logo} alt="Logo" className="h-24 w-auto" />
            {/* <span className="text-xl font-bold text-white">Praceria</span> */}
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-4 right-4 lg:hidden text-white hover:bg-white/20"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Scrollable Content Container */}
        <div className="flex-1">
          {/* Admin Profile Section */}
          <div className="px-6 py-6 border-b border-white/20">
            <div className="flex items-center">
              <AccountMenu />
            </div>

            {/* Admin Stats */}
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <p className="text-xs text-white/70">Área do</p>
                <p className="text-lg font-bold text-white">Organizador</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3 text-center">
                <p className="text-xs text-white/70">Sistema</p>
                <p className="text-lg font-bold text-white">Admin</p>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-center gap-2 rounded-full bg-white/10 px-3 py-1.5">
              <span
                className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${
                  whatsappConnected === null
                    ? "bg-white/40"
                    : whatsappConnected
                      ? "bg-green-400"
                      : "bg-red-400"
                }`}
              />
              <span className="text-xs font-medium text-white/80">
                WhatsApp:{" "}
                {whatsappConnected === null
                  ? "Verificando..."
                  : whatsappConnected
                    ? "Conectado"
                    : "Desconectado"}
              </span>
            </div>

            <div className="mt-4">
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-4">
                <a
                  href={getInstagram().href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-start gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition w-full min-w-0"
                >
                  <Instagram className="h-4 w-4 flex-shrink-0" />
                  <span
                    className="text-sm truncate"
                    title={getInstagram().display}
                  >
                    {getInstagram().display}
                  </span>
                </a>
                <a
                  href={getTikTok().href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 256 256"
                    className="h-4 w-4"
                    fill="currentColor"
                  >
                    <path d="M168.2 34.8c10.9 15.6 26.5 24.3 45.4 25.1v33.4c-17.3-.9-33.9-6.9-48.3-17.1v70.8c0 36.9-29.9 66.8-66.8 66.8s-66.8-29.9-66.8-66.8c0-36.9 29.9-66.8 66.8-66.8 5.1 0 10.1.6 14.9 1.8v35.1c-4.7-2.1-9.9-3.2-15.3-3.2-17.9 0-32.5 14.6-32.5 32.5s14.6 32.5 32.5 32.5 32.5-14.6 32.5-32.5V24h37.6z" />
                  </svg>
                  <span className="text-sm">{getTikTok().display}</span>
                </a>
                <a
                  href={getLinkedin().href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
                >
                  <Linkedin className="h-4 w-4" />
                  <span className="text-sm">{getLinkedin().display}</span>
                </a>
                <a
                  href={getFacebook().href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white hover:bg-white/20 transition"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                  >
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                  </svg>
                  <span className="text-sm">{getFacebook().display}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="px-4 py-6 space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.path);

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`
                    flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group
                    ${
                      isActive
                        ? "bg-white text-[#24324D] shadow-lg"
                        : "text-white/90 hover:bg-white/10 hover:text-white"
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon
                    className={`mr-3 h-5 w-5 ${
                      isActive
                        ? "text-[#24324D]"
                        : "text-white/90 group-hover:text-white"
                    }`}
                  />
                  {item.title}
                </Link>
              );
            })}

            {/* Secondary Navigation */}
            <div className="pt-4 space-y-2">
              <div className="px-4 py-2">
                <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                  Configurações Gerais
                </h3>
              </div>
              {secondaryNavigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.path);

                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    className={`
                      flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 group
                      ${
                        isActive
                          ? "bg-white text-[#24324D] shadow-lg"
                          : "text-white/90 hover:bg-white/10 hover:text-white"
                      }
                    `}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon
                      className={`mr-3 h-5 w-5 ${
                        isActive
                          ? "text-[#24324D]"
                          : "text-white/90 group-hover:text-white"
                      }`}
                    />
                    {item.title}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Actions - Fixed at bottom */}
        <div className="px-4 py-4 border-t border-white/20 space-y-2 flex-shrink-0">
          <Button
            variant="ghost"
            className="w-full justify-start text-white/90 hover:bg-white/10 hover:text-white"
            onClick={handleLogout}
          >
            <LogOut className="mr-3 h-5 w-5" />
            Sair
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="overflow-y-auto flex-1 lg:ml-0">
        {/* Mobile menu button */}
        <div className="lg:hidden sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between gap-x-4 border-b border-gray-200 bg-[#24324D] px-4 text-white">
          <Button
            variant="ghost"
            size="sm"
            className="absolute left-3 top-3"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="h-[40px] w-[40px]"></div>

          <span className="text-lg font-semibold">Área do Organizador</span>

          <img
            src={logo}
            alt="Praceria"
            style={{
              height: 40,
              width: "auto",
            }}
          />
        </div>

        {/* Page content */}
        <main className="p-2 sm:p-4 lg:p-8">
          <div className="w-full">
            <Card className="bg-white border-0">
              <CardContent className="p-4 md:p-6 lg:p-8">
                {children}
                <Outlet />
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
